/**
 * /admin/ledgers — browse imported master ledgers.
 * @file src/web/features/admin/pages/ledgers.tsx
 *
 * Searchable, paginated table of ledgers (name, group, opening balance
 * with Dr/Cr, GSTIN) from GET /api/tally-import/ledgers. A row opens that
 * ledger's statement.
 *
 * This is the TRANSACTIONAL view and it owns ledger creation. The structural
 * view — the same ledgers under their groups, as a tree — is Masters → Chart
 * of accounts, which links here rather than repeating the list. Keep it that
 * way: two places that create ledgers is two places to get `source: 'manual'`
 * wrong.
 */

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Search, ChevronLeft, ChevronRight, Plus, Pencil, Network } from 'lucide-react';
import { SEO } from '../../../shared/components';
import { AdminPageHeader } from '../components/AdminPageHeader';
import { PageLoading } from '../components/PageLoading';
import { adminFetchJson } from '../lib/admin-api';
import { useCompany } from '../lib/company-context';
import { LedgerModal, type LedgerLite } from '../components/LedgerModal';
import { OpeningAmountCells } from '../components/OpeningBalanceCell';

interface Ledger { id: string; name: string; parent: string | null; openingBalance: number | null; gstin: string | null; source?: string; edited?: boolean }
interface Page { total: number; page: number; pageSize: number; items: Ledger[] }

export default function LedgersPage() {
  const navigate = useNavigate();
  const { entityId } = useCompany();
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState<Page | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshKey, setRefreshKey] = useState(0);
  const [modalL, setModalL] = useState<null | { ledger: LedgerLite | null }>(null);

  useEffect(() => { setPage(1); }, [entityId]);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    const t = setTimeout(() => {
      adminFetchJson<Page>('/api/tally-import/ledgers', { query: { q, page, entityId: entityId ?? undefined } })
        .then((d) => !cancel && setData(d))
        .finally(() => !cancel && setLoading(false));
    }, q ? 250 : 0);
    return () => { cancel = true; clearTimeout(t); };
  }, [q, page, entityId, refreshKey]);

  const pages = data ? Math.ceil(data.total / data.pageSize) : 1;

  return (
    <>
      <SEO title="Ledgers" />
      <AdminPageHeader title="Ledgers" breadcrumbs={[{ label: 'Books', href: '/admin/books' }, { label: 'Ledgers' }]} />

      <div className="mb-4 flex items-center justify-between gap-3">
        <div className="relative w-full max-w-sm">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-ink-400" />
          <input
            value={q}
            onChange={(e) => { setPage(1); setQ(e.target.value); }}
            placeholder="Search ledgers…"
            className="w-full rounded-xl border border-ink-200 bg-paper py-2.5 pl-10 pr-4 text-sm shadow-sm focus:border-inkblack focus:outline-none focus:ring-2 focus:ring-highlighter"
          />
        </div>
        <div className="flex items-center gap-3">
          {data && <span className="shrink-0 text-sm text-ink-500">{data.total.toLocaleString('en-IN')} ledgers</span>}
          <Link
            to="/admin/masters?tab=chart"
            title="See these ledgers under their groups"
            className="inline-flex shrink-0 items-center gap-2 rounded-full border border-ink-300 bg-paper px-4 py-2 text-sm font-semibold text-ink-700 no-underline transition hover:bg-ink-50"
          >
            <Network className="h-4 w-4" /> Chart of accounts
          </Link>
          <button onClick={() => setModalL({ ledger: null })} className="inline-flex items-center gap-2 rounded-full bg-inkblack px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:-translate-y-0.5">
            <Plus className="h-4 w-4" /> New ledger
          </button>
        </div>
      </div>

      {loading && !data ? (
        <PageLoading label="Loading ledgers" />
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-paper">
          <table className="w-full min-w-[36rem] text-sm">
            <thead>
              <tr className="border-b border-ink-100 text-left text-xs uppercase tracking-wide text-ink-400">
                <th className="px-5 py-3 font-medium">Ledger</th>
                <th className="px-5 py-3 font-medium">Group</th>
                <th className="px-5 py-3 text-right font-medium">Debit</th>
                <th className="px-5 py-3 text-right font-medium">Credit</th>
                <th className="px-5 py-3 font-medium">GSTIN</th>
                <th className="px-5 py-3 text-right font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-ink-50">
              {data?.items.map((l) => (
                <tr key={l.id} onClick={() => navigate(`/admin/ledgers/${l.id}`)} className="cursor-pointer hover:bg-ink-50/60">
                  <td className="px-5 py-3 font-medium text-ink-900">
                    {l.name}
                    {l.source === 'manual' && <span className="ml-1.5 rounded-full bg-highlighter-40 px-1.5 py-0.5 text-[10px] font-medium text-inkblack">manual</span>}
                    {l.source !== 'manual' && l.edited && <span className="ml-1.5 rounded-full bg-warn-50 px-1.5 py-0.5 text-[10px] font-medium text-warn-600">edited</span>}
                  </td>
                  <td className="px-5 py-3 text-ink-500">{l.parent ?? '—'}</td>
                  {/* Read-only — the pencil at the end of the row opens the
                      ledger, where the balance and its Dr/Cr side are edited. */}
                  <OpeningAmountCells value={l.openingBalance} padding="px-5 py-3" />
                  <td className="px-5 py-3 text-ink-500">{l.gstin || <span className="text-ink-300">—</span>}</td>
                  <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                    <button onClick={() => setModalL({ ledger: l })} title="Edit ledger" className="grid size-7 place-items-center rounded-lg text-ink-300 hover:bg-ink-100 hover:text-ink-600"><Pencil className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
              {data && data.items.length === 0 && (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-ink-400">No ledgers found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {data && pages > 1 && <Pager page={data.page} pages={pages} onPage={setPage} />}

      {modalL && <LedgerModal entityId={entityId} ledger={modalL.ledger} onClose={() => setModalL(null)} onSaved={() => { setModalL(null); setRefreshKey((k) => k + 1); }} />}
    </>
  );
}

export function Pager({ page, pages, onPage }: { page: number; pages: number; onPage: (p: number) => void }) {
  return (
    <div className="mt-4 flex items-center justify-end gap-2 text-sm">
      <button disabled={page <= 1} onClick={() => onPage(page - 1)} className="inline-flex items-center gap-1 rounded-lg border border-ink-200 bg-paper px-3 py-1.5 text-ink-600 disabled:opacity-40 hover:bg-ink-50">
        <ChevronLeft className="h-4 w-4" /> Prev
      </button>
      <span className="px-2 text-ink-500">Page {page} of {pages}</span>
      <button disabled={page >= pages} onClick={() => onPage(page + 1)} className="inline-flex items-center gap-1 rounded-lg border border-ink-200 bg-paper px-3 py-1.5 text-ink-600 disabled:opacity-40 hover:bg-ink-50">
        Next <ChevronRight className="h-4 w-4" />
      </button>
    </div>
  );
}
